import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import type { ProposalProductLineCardProps } from "@/components/proposal/ProposalProductLineCard";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";
import { parseDtoolsLineCatalogMeta, resolveLineDisplayImage, resolveStagingLineVisibleDescription } from "@shared/dtoolsCatalogueMatch";
import { formatProductBrandModelReference } from "@shared/productDisplayName";
import {
  resolveInteractiveProposalLineCopy,
  resolveProposalLineCustomerTitle,
} from "@shared/proposalLinePresentation";
import {
  parseCustomerExplanationStored,
  resolveProposalItemHoverExplanation,
  type CleoItemHoverExplanation,
  type ProposalItemHoverSource,
} from "@shared/proposalItemCleoDescription";
import {
  renderPricingVisibility,
  renderSupportingComponentBadge,
  renderVoLineAnnotation,
  renderVoQuantityDisplay,
  type VoLiveAnnotation,
} from "@shared/rendering";
import type { DtoolsStagingLineWithVo } from "@shared/variationOrderApplyToLiveView";

export type DtoolsLineHoverTrace = {
  id: string;
  name: string | null;
  manufacturer: string | null;
  model: string | null;
  cleoDescriptionPresent: boolean;
  polishedDescriptionPresent: boolean;
  customerExplanationRaw: string | null;
  customerExplanationParsed: CleoItemHoverExplanation | null;
  hoverSource: ProposalItemHoverSource;
  passedToCard: string | null;
};

export function buildDtoolsLineHoverTrace(line: DtoolsStagingLine): DtoolsLineHoverTrace {
  const raw = String(line.customerExplanation ?? "").trim() || null;
  const parsed = parseCustomerExplanationStored(line.customerExplanation);
  const visible = resolveStagingLineVisibleDescription(line);
  const title = resolveProposalLineCustomerTitle(line);
  const resolved = resolveProposalItemHoverExplanation({
    customerExplanation: line.customerExplanation,
    visibleDescription: visible,
    productTitle: title,
  });
  return {
    id: line.id,
    name: line.displayName ?? null,
    manufacturer: line.manufacturer,
    model: line.model,
    cleoDescriptionPresent: Boolean(String(line.cleoDescription ?? "").trim()),
    polishedDescriptionPresent: Boolean(String(line.polishedDescription ?? "").trim()),
    customerExplanationRaw: raw,
    customerExplanationParsed: parsed,
    hoverSource: resolved.source,
    passedToCard: raw,
  };
}

export function shouldShowStaffHoverFallbackHint(line: DtoolsStagingLine): boolean {
  const visible = resolveStagingLineVisibleDescription(line);
  const title = resolveProposalLineCustomerTitle(line);
  const resolved = resolveProposalItemHoverExplanation({
    customerExplanation: line.customerExplanation,
    visibleDescription: visible,
    productTitle: title,
  });
  return resolved.source !== "cleo";
}

export function dtoolsStagingLineToProductCardProps(
  line: DtoolsStagingLine,
  options?: {
    indent?: boolean;
    compact?: boolean;
    hidePrice?: boolean;
    hideImage?: boolean;
    compactCopy?: boolean;
    showCleoDescriptionPending?: boolean;
    cleoDescriptionPendingMessage?: string;
    showStaffHoverDiagnostics?: boolean;
    debugHoverTrace?: boolean;
    enableImageLightbox?: boolean;
    pdfPremiumLayout?: boolean;
  },
): ProposalProductLineCardProps {
  const brand = String(line.manufacturer ?? "").trim();
  const model = String(line.model ?? "").trim();
  const storedDisplayName = String(line.displayName ?? "").trim();
  const { title, description: cleoDescription } = resolveInteractiveProposalLineCopy(line);
  const technicalRef = formatProductBrandModelReference({
    brand: line.manufacturer,
    model: line.model,
  });
  const subtitle = technicalRef && technicalRef !== title ? technicalRef : null;
  const pdfPremiumLayout = options?.pdfPremiumLayout === true;

  const catalogMeta = parseDtoolsLineCatalogMeta(line);
  const imageUrl = options?.hideImage ? null : resolveLineDisplayImage(line.imageUrl, catalogMeta) || null;
  const customerExplanation = line.customerExplanation ?? null;
  const voAnn = (line as DtoolsStagingLineWithVo).variationOrderAnnotation as VoLiveAnnotation | undefined;

  if (options?.debugHoverTrace) {
    console.info("[dtools-hover-trace]", buildDtoolsLineHoverTrace(line));
  }

  const annotation = renderVoLineAnnotation(voAnn);
  const supportingBadge = renderSupportingComponentBadge(Boolean(line.isAccessory));
  const { hidePrice } = renderPricingVisibility({
    hideItemPricing: options?.hidePrice,
    forceHide: voAnn?.kind === "removed",
  });

  return {
    variant: "project",
    imageUrl,
    imageAlt: title,
    title,
    subtitle,
    description: null,
    cleoDescription,
    customerExplanation,
    quantity: renderVoQuantityDisplay({ quantity: line.quantity, ann: voAnn }),
    priceFormatted: formatDtoolsMoney(line.lineTotal),
    hidePrice,
    statusBadge: annotation.statusBadge,
    badge: annotation.badge ?? supportingBadge,
    metaLine: annotation.metaLine,
    changeReason: annotation.changeReason,
    className: voAnn?.kind === "removed" ? "opacity-60" : undefined,
    indent: options?.indent,
    compact: options?.compact,
    showCleoDescriptionPending: options?.showCleoDescriptionPending,
    cleoDescriptionPendingMessage: options?.cleoDescriptionPendingMessage,
    showStaffHoverDiagnostics: options?.showStaffHoverDiagnostics ?? false,
    enableImageLightbox: options?.enableImageLightbox,
    compactCopy: options?.compactCopy,
    pdfPremiumLayout,
    productBrand: brand || null,
    productModel: model || null,
    productDisplayName: storedDisplayName || null,
  };
}
